import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useEmergencyCreditsConfig } from "@src/appV2/redesign/EmergencyCredits/hooks/useEmergencyCreditsConfig";
import { SHIFT_CANCEL_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { isAfter } from "date-fns";
import { useCallback, useMemo } from "react";
import { generatePath, useHistory } from "react-router-dom";

import type { CancelShiftBottomSheetProps } from "../components/CancelShiftBottomSheet";

interface UseCancelShiftFlowParams {
  shiftId: string | undefined;
  shiftStartDate: Date | undefined;
  currentDate: Date;
  clockInStartIso?: string | null | undefined;
  orientationBlockId?: string | null | undefined;
  onShiftCancelled?: () => void;
}

export interface UseCancelShiftFlowResult {
  startCancelFlow: () => void;
  cancelShiftBottomSheetProps: CancelShiftBottomSheetProps | undefined;
}

/**
 * Hook that manages the cancel shift flow, handling both the legacy bottom sheet
 * and the new full-page experience based on the Emergency Credits feature flag.
 *
 * - When Emergency Credits is enabled: `startCancelFlow` navigates to the cancel page,
 *   and `cancelShiftBottomSheetProps` is `undefined`.
 * - Otherwise: `startCancelFlow` opens the bottom sheet modal,
 *   and `cancelShiftBottomSheetProps` contains the props for `CancelShiftBottomSheet`.
 */
export function useCancelShiftFlow(params: UseCancelShiftFlowParams): UseCancelShiftFlowResult {
  const {
    shiftId,
    shiftStartDate,
    currentDate,
    clockInStartIso,
    orientationBlockId,
    onShiftCancelled,
  } = params;

  const modalState = useModalState();
  const emergencyCreditsConfig = useEmergencyCreditsConfig();
  const isEmergencyCreditsEnabled = emergencyCreditsConfig.enabled;
  const history = useHistory();

  const isNoCallNoShow = useMemo(() => {
    if (!isDefined(shiftStartDate)) {
      return false;
    }

    if (isDefined(clockInStartIso)) {
      return false;
    }

    return isAfter(currentDate, shiftStartDate);
  }, [clockInStartIso, currentDate, shiftStartDate]);

  const isOrientationBlockShift = isDefined(orientationBlockId);
  const canUseEmergencyCreditsFlow = isEmergencyCreditsEnabled;

  const startCancelFlow = useCallback(() => {
    if (canUseEmergencyCreditsFlow && isDefined(shiftId)) {
      history.push(generatePath(SHIFT_CANCEL_FULL_PATH, { shiftId }));
    } else if (!canUseEmergencyCreditsFlow) {
      modalState.openModal();
    }
  }, [canUseEmergencyCreditsFlow, shiftId, history, modalState]);

  const cancelShiftBottomSheetProps = useMemo((): CancelShiftBottomSheetProps | undefined => {
    // Page flow - no bottom sheet needed
    if (canUseEmergencyCreditsFlow) {
      return undefined;
    }

    // Bottom sheet flow - return props if we have valid data
    if (!isDefined(shiftStartDate) || !isDefined(shiftId)) {
      return undefined;
    }

    return {
      modalState,
      isNoCallNoShow,
      shiftStartDate,
      shiftId,
      currentDate,
      isOrientationBlockShift,
      onShiftCancelled,
    };
  }, [
    canUseEmergencyCreditsFlow,
    shiftStartDate,
    shiftId,
    modalState,
    isNoCallNoShow,
    currentDate,
    isOrientationBlockShift,
    onShiftCancelled,
  ]);

  return {
    startCancelFlow,
    cancelShiftBottomSheetProps,
  };
}
